import LoadingButton from "@mui/lab/LoadingButton";
import { Box, Button, Grid, Typography } from "@mui/material";
import { useGoogleLogin } from "@react-oauth/google";
import { AlertContext } from "contexts/Alert";
import { FairPaySurveyResponseContext } from "contexts/FairPaySurveyResponse";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "schemas/routes";

import { fetcher } from "utils/api";
import { trackEvent } from "utils/tracking";

import FairPayTextField from "../components/FairPayTextField";

const styles = {
  button: {
    px: 3,
    py: 2,
    textTransform: "none",
    borderRadius: 20,
    fontSize: 18,
    my: 2,
  },
};

const FairPayConnectGmail = () => {
  const { setAlert } = useContext(AlertContext);
  const { fairPaySurveyResponse: survey, setFairPaySurveyResponse } =
    useContext(FairPaySurveyResponseContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [skipConnect, setSkipConnect] = useState<boolean>(false);
  const navigate = useNavigate();

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async (tokenResponse) => {
      try {
        setLoading(true);
        await fetcher(
          `/api/organization/fair-pay-survey-responses/${survey?.surveyUuid}/oauth/callback`,
          "POST",
          {},
          tokenResponse,
        );
        await generateReport();
      } catch (error) {
        setAlert(
          error.message ? error.message : "Unable to connect with Google",
          "error",
        );
      } finally {
        setLoading(false);
      }
    },
    onError: () => {
      setAlert("Unable to connect with Google", "error");
      setLoading(false);
      setSkipConnect(true);
    },
    hint: survey?.email || undefined,
    scope:
      "https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/gmail.readonly",
  });

  const handleConnectToGoogle = () => {
    trackEvent("Fair Pay - Connect to Gmail Clicked", {
      "Survey UUID": survey?.surveyUuid,
    });
    googleLogin();
  };

  const handleSkipConnect = () => {
    trackEvent("Fair Pay - Skip Connect to Gmail Clicked", {
      "Survey UUID": survey?.surveyUuid,
    });
    setSkipConnect(true);
  };

  const saveEmail = async () => {
    trackEvent("Fair Pay - Save Email Button Clicked", {
      "Survey UUID": survey?.surveyUuid,
    });
    setLoading(true);
    try {
      await fetcher(
        `/api/organization/fair-pay-survey-responses/${survey?.surveyUuid}`,
        "PUT",
        {},
        { email: survey?.email },
      );
      await generateReport();
    } catch (error) {
      setAlert(
        error.message ? error.message : "Unable to save email address",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    trackEvent("Fair Pay - Report Generated", {
      "Survey UUID": survey?.surveyUuid,
    });
    setLoading(true);
    const generateResponse = await fetcher(
      `/api/organization/fair-pay-survey-responses/${survey?.surveyUuid}/generate`,
      "POST",
    );
    setFairPaySurveyResponse(generateResponse.surveyResponse);
    navigate(`/${routes.fairpay}/${survey?.surveyUuid}/results`);
    setLoading(false);
  };

  return (
    <Box>
      {skipConnect ? (
        <Box>
          <Typography variant="h4" textAlign={"center"} sx={{ mb: 2 }}>
            Email Address
          </Typography>
          <Typography textAlign="center" sx={{ mb: 2 }}>
            Type your email address below so we can send you the report when
            it's ready.
          </Typography>
          <FairPayTextField
            placeholder="Your email address *"
            name="email"
            value={survey?.email || ""}
          />

          <LoadingButton
            disableElevation
            sx={styles.button}
            variant="contained"
            color="primary"
            fullWidth
            onClick={saveEmail}
            disabled={!survey?.email}
            loading={loading}
          >
            See Report
          </LoadingButton>
          <Button
            sx={{ textTransform: "none" }}
            onClick={() => setSkipConnect(false)}
            fullWidth
          >
            Connect to Gmail instead
          </Button>
        </Box>
      ) : (
        <>
          <Typography variant="h4" textAlign={"center"} sx={{ mb: 2 }}>
            {survey?.integrationEmail ? (
              <>
                <Box
                  component="i"
                  className="fa-solid fa-circle-check"
                  sx={{ color: "green.main" }}
                />{" "}
                Successfully Connected
              </>
            ) : (
              "Connect Your Gmail"
            )}
          </Typography>

          {!survey?.integrationEmail ? (
            <>
              <Typography textAlign="center">
                Connecting your Gmail will help us show you a breakdown on how
                specific brands have paid you compared to other creators. This
                part only works with Gmail accounts right now! If you're using
                another email, skip this step for now.
              </Typography>
              <LoadingButton
                variant="contained"
                color="primary"
                sx={styles.button}
                fullWidth
                loading={loading}
                onClick={handleConnectToGoogle}
                disableElevation
              >
                Let's go
              </LoadingButton>
              <Grid container justifyContent="center" alignItems="center">
                <Button
                  sx={{ textTransform: "none" }}
                  onClick={handleSkipConnect}
                >
                  Skip
                </Button>
              </Grid>
            </>
          ) : (
            <>
              <Typography textAlign={"center"}>
                Click the button to see the report.
              </Typography>

              <Button
                disableElevation
                sx={styles.button}
                variant="contained"
                color="primary"
                fullWidth
                href={`/${routes.fairpay}/${survey?.surveyUuid}/results`}
                onClick={() => {
                  trackEvent("Fair Pay - See Report Button Clicked", {
                    "Survey UUID": survey?.surveyUuid,
                  });
                }}
              >
                See Report
              </Button>
            </>
          )}
        </>
      )}
    </Box>
  );
};
export default FairPayConnectGmail;
